import React, { useState, useEffect, useRef, useMemo } from 'react';
import { noteColor } from '../utils/userColors';
import AddTaskButton from './tasks/AddTaskButton';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';
import { Order, OrderItem, Warehouse, ShippingCarrier, StockItem, Shipment, ShipmentItem } from '../types';
import ShippingDialog from './ShippingDialog';
import { normalizeCarrier, getTrackingLink } from '../utils/carrierUtils';
import { hasAddressConflict, buildAddressConfirmationNote, ADDRESS_CONFIRMATION_OPENER } from '../utils/addressUtils';
import '../OrderDetailsDialog.css';
import '../core.css';

interface OrderDetailsDialogProps {
  order: Order;
  onClose: () => void;
  warehouses: Warehouse[];
  refreshOrders: () => Promise<void>;
}

interface OrderNote {
  id: number;
  order_id: number;
  note: string;
  added_by: string;
  date_added: string;
  is_customer_note: boolean;
}

const OrderDetailsDialog: React.FC<OrderDetailsDialogProps> = ({ order, onClose, warehouses, refreshOrders }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [orderNotes, setOrderNotes] = useState<OrderNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'Customer Note' | 'Private Note'>('Private Note');
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [newTrackingNumber, setNewTrackingNumber] = useState('');
  const [newCarrier, setNewCarrier] = useState('');
  const [carriers, setCarriers] = useState<ShippingCarrier[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | null>(null);
  const [newShipmentReference, setNewShipmentReference] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectedNonItemShipments, setSelectedNonItemShipments] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<{updates: any, message: string} | null>(null);
  const [orderStatus, setOrderStatus] = useState(order.status);
  const [showTrackingForm, setShowTrackingForm] = useState(false);
  const [showCustomerNoteDialog, setShowCustomerNoteDialog] = useState(false);
  const [customerNote, setCustomerNote] = useState('');
  const [showShippingDialog, setShowShippingDialog] = useState(false);
  const [showReshipPrompt, setShowReshipPrompt] = useState(false);
  const [itemStocks, setItemStocks] = useState<Map<number, StockItem>>(new Map());
  const [alreadyShippedWarning, setAlreadyShippedWarning] = useState<string | null>(null);
  const [assigningShipmentId, setAssigningShipmentId] = useState<number | null>(null);
  const [assignmentItems, setAssignmentItems] = useState<ShipmentItem[]>([]);

  const [hideAdminNotes, setHideAdminNotes] = useState(true);
  const [userColors, setUserColors] = useState<Map<string, string>>(new Map());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');
  const [pendingDelete, setPendingDelete] = useState<{order_id: number, shipment_id: number} | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const [showAddressConfirm, setShowAddressConfirm] = useState(false);
  const [addressConfirmText, setAddressConfirmText] = useState('');
  const [sendingAddressConfirm, setSendingAddressConfirm] = useState(false);

  const addressConflict = hasAddressConflict(order);
  // Most recent confirmation request already sent to the customer, if any.
  const lastAddressConfirmation = orderNotes
    .filter((n) => n.is_customer_note && n.note.trim().startsWith(ADDRESS_CONFIRMATION_OPENER))
    .sort((a, b) => new Date(b.date_added).getTime() - new Date(a.date_added).getTime())[0];

  const openAddressConfirm = () => {
    setAddressConfirmText(buildAddressConfirmationNote(order));
    setShowAddressConfirm(true);
  };

  // Sent as a WooCommerce customer note, so WooCommerce emails it to the
  // customer from the store address and replies go to the store inbox.
  const handleSendAddressConfirm = async () => {
    const note = addressConfirmText.trim();
    if (!note || sendingAddressConfirm) return;
    setSendingAddressConfirm(true);
    try {
      showToast('Emailing customer...', 'syncing');
      const response = await api.post('/add-order-note', {
        order_id: order.id,
        note,
        added_by: user?.email || 'unknown',
        is_customer_note: true,
      });
      setOrderNotes((prev) => [...prev, {
        id: response.data.id || Date.now(),
        order_id: order.id,
        note,
        added_by: user?.email || 'unknown',
        date_added: new Date().toISOString(),
        is_customer_note: true,
      }]);
      await syncWithWooCommerce({ notes: [{ content: note, added_by: 'MPP ERP System', isCustomerNote: true }] });
      setShowAddressConfirm(false);
      showToast('Address confirmation sent to customer', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to send address confirmation';
      setError(errorMessage);
      console.error('Address confirmation error:', err);
      showToast(errorMessage, 'error');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSendingAddressConfirm(false);
    }
  };

  const selectedItemsArray = useMemo(() => Array.from(selectedItems), [selectedItems]);

  const fetchData = async () => {
    try {
      const cacheBuster = Date.now();
      const [notesResponse, shipmentsResponse, orderResponse, carriersResponse, userPermissionsResponse] = await Promise.all([
        api.get(`/order-notes?order_id=${order.id}&_=${cacheBuster}`),
        api.get(`/shipments-query?order_id=${order.id}&_=${cacheBuster}`),
        api.get(`/orders/${order.id}?_=${cacheBuster}`),
        api.get(`/shipping-carriers?_=${cacheBuster}`),
        api.get('/user-permissions'),
      ]);
      console.log('fetchData responses:', { notesResponse, shipmentsResponse, orderResponse, carriersResponse });
      const notes = Array.isArray(notesResponse.data.notes) ? notesResponse.data.notes.map((note: any) => ({
        ...note,
        is_customer_note: note.is_customer_note ?? false,
      })) : [];
      setOrderNotes(notes);
      const fetchedShipments = Array.isArray(shipmentsResponse.data?.shipments) ? shipmentsResponse.data.shipments : [];
      setShipments(fetchedShipments);
      setCarriers(Array.isArray(carriersResponse.data) ? carriersResponse.data : []);
      // Default to the first carrier only when none is picked yet, so a reload
      // never overwrites the user's choice.
      setNewCarrier((prev) => prev || carriersResponse.data[0]?.name || '');

      setOrderStatus(orderResponse.data.status);

      const userColorsMap = new Map<string, string>();
      userPermissionsResponse.data.users.forEach((u: any) => {
        userColorsMap.set(u.email, noteColor(u.user_color));
      });
      setUserColors(userColorsMap);

      const stockPromises = order.items.map(item =>
        api.get(`/product-stock?product_id=${item.product_id}&variation_id=${item.variation_id}&_=${cacheBuster}`)
          .then(response => ({ itemId: item.id, stock: response.data }))
          .catch(err => {
            console.error(`Stock fetch error for item ${item.id}:`, err);
            return { itemId: item.id, stock: {} };
          })
      );
      const stockResults = await Promise.all(stockPromises);
      const stockMap = new Map<number, StockItem>();
      stockResults.forEach(({ itemId, stock }) => stockMap.set(itemId, stock));
      setItemStocks(stockMap);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to load order details';
      setError(errorMessage);
      console.error('Fetch order details error:', err.message, err.stack);
      showToast(errorMessage, 'error');
      setTimeout(() => setError(null), 5000);
    }
  };

  // The parent passes a new onClose on every render (and it re-renders on
  // background polls). Keep it in a ref so those renders don't re-run the load
  // below, which used to reset the form mid-entry.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    fetchData();
  }, [order.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onCloseRef.current();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddTracking = async (addCustomerNote: boolean = false) => {
    if (!newTrackingNumber.trim() || !newCarrier || selectedWarehouse === null) {
      setError('Please provide tracking number, carrier, and warehouse');
      showToast('Please provide tracking number, carrier, and warehouse', 'error');
      setTimeout(() => setError(null), 5000);
      return;
    }
    try {
      console.log(`Adding shipment for order ${order.id}:`, {
        tracking_number: newTrackingNumber,
        carrier: newCarrier,
        warehouse_id: selectedWarehouse,
        shipment_reference: newShipmentReference || `TRK-${newTrackingNumber}`,
      });
      const selectedInstanceKeys = Array.from(selectedItems);
      if (selectedInstanceKeys.length === 0) {
        setError('Please select at least one item');
        showToast('Please select at least one item', 'error');
        setTimeout(() => setError(null), 5000);
        return;
      }

      // Count instances per order_item_id
      const itemIdCounts: { [key: number]: number } = {};
      selectedInstanceKeys.forEach((key) => {
        const [itemId] = key.split('-').map(Number);
        itemIdCounts[itemId] = (itemIdCounts[itemId] || 0) + 1;
      });

      // Create shipment items with instance_number
      const items: ShipmentItem[] = [];
      const itemInstanceCounts: { [key: number]: number } = {};
      Object.entries(itemIdCounts).forEach(([itemId, totalQuantity]) => {
        const item = order.items.find(i => i.id === Number(itemId));
        if (!item) {
          console.warn(`Item ${itemId} not found in order.items`);
          return;
        }
        console.log(`Processing item ${itemId}: ${item.product_name}, Total Quantity: ${totalQuantity}`);
        itemInstanceCounts[Number(itemId)] = itemInstanceCounts[Number(itemId)] || 0;
        for (let i = 0; i < totalQuantity; i++) {
          itemInstanceCounts[Number(itemId)]++;
          items.push({
            order_item_id: item.id,
            quantity: 1,
            product_id: item.product_id,
            product_name: item.product_name,
            sku: item.sku || null,
            gtin: item.gtin || null,
            instance_number: itemInstanceCounts[Number(itemId)], // Instance number (1, 2, 3, ...)
            id: 0,
            shipment_id: 0,
            status: 'Pending',
            scan_timestamp: null,
            scanned_by: null,
          });
        }
      });

      console.log('Items to send to /shipments:', items);

      showToast('Creating shipment...', 'syncing');
      const response = await api.post('/shipments', {
        action: 'create',
        order_id: order.id,
        data: {
          warehouse_id: selectedWarehouse,
          tracking_number: newTrackingNumber,
          carrier: newCarrier,
          date_shipped: new Date().toISOString(),
          shipment_reference: newShipmentReference || `TRK-${newTrackingNumber}`,
          items,
        },
      });
      setShipments([...shipments, response.data.shipment]);

      showToast('Sending tracking to WooCommerce...', 'syncing');
      await syncWithWooCommerce({
        tracking_numbers: [{
          tracking_number: newTrackingNumber,
          carrier: newCarrier,
          date_shipped: new Date().toISOString(),
        }],
      });

      if (addCustomerNote && customerNote.trim()) {
        showToast('Adding customer note...', 'syncing');
        const noteResponse = await api.post('/add-order-note', {
          order_id: order.id,
          note: customerNote,
          added_by: user?.email || 'MPP ERP System',
          is_customer_note: true,
        });
        const newNoteObj: OrderNote = {
          id: noteResponse.data.id || Date.now(),
          order_id: order.id,
          note: customerNote,
          added_by: user?.email || 'MPP ERP System',
          date_added: new Date().toISOString(),
          is_customer_note: true,
        };
        setOrderNotes([...orderNotes, newNoteObj]);
      }

      const shippedQuantities: { [itemId: number]: number } = {};
      shipments.forEach((s: Shipment) => {
        s.items.forEach((si: ShipmentItem) => {
          shippedQuantities[si.order_item_id] = (shippedQuantities[si.order_item_id] || 0) + 1; // Count instances
        });
      });
      items.forEach((item) => {
        shippedQuantities[item.order_item_id] = (shippedQuantities[item.order_item_id] || 0) + 1; // Count instances
      });

      const allItemsShipped = order.items.every((item) => {
        const totalShipped = shippedQuantities[item.id] || 0;
        return totalShipped >= item.quantity;
      });
      const newStatus = allItemsShipped ? 'completed' : 'partial-shipped';

      showToast('Updating order status...', 'syncing');
      await api.post('/update-order-status', {
        order_id: order.id,
        status: newStatus,
      });
      setOrderStatus(newStatus);

      showToast('Sending order details to WooCommerce...', 'syncing');
      await syncWithWooCommerce({
        status: newStatus,
        notes: addCustomerNote && customerNote.trim()
          ? [{ content: customerNote, added_by: 'MPP ERP System', isCustomerNote: true }]
          : [],
      });

      setNewTrackingNumber('');
      setNewCarrier(carriers[0]?.name || '');
      setSelectedWarehouse(null);
      setNewShipmentReference('');
      setShowTrackingForm(false);
      setShowCustomerNoteDialog(false);
      setCustomerNote('');
      setSelectedItems(new Set());
      setAlreadyShippedWarning(null);
      await refreshOrders();
      await fetchData();
      showToast('Shipment added successfully', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to add shipment';
      setError(errorMessage);
      console.error('Add shipment error:', err);
      showToast(errorMessage, 'error');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      console.log(`Adding note for order ${order.id}: ${newNote}`);
      const isCustomerNote = noteType === 'Customer Note';
      showToast('Adding note...', 'syncing');
      const response = await api.post('/add-order-note', {
        order_id: order.id,
        note: newNote,
        added_by: user?.email || 'unknown',
        is_customer_note: isCustomerNote,
      });
      const newNoteObj: OrderNote = {
        id: response.data.id || Date.now(),
        order_id: order.id,
        note: newNote,
        added_by: user?.email || 'unknown',
        date_added: new Date().toISOString(),
        is_customer_note: isCustomerNote,
      };
      setOrderNotes([...orderNotes, newNoteObj]);
      showToast('Sending note to WooCommerce...', 'syncing');
      await syncWithWooCommerce({ notes: [{ content: newNote, added_by: 'MPP ERP System', isCustomerNote }] });
      setNewNote('');
      setNoteType('Private Note');
      showToast('Note added successfully', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to add note';
      setError(errorMessage);
      console.error('Add note error:', err);
      showToast(errorMessage, 'error');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeleteShipment = async (shipmentId: number, force = false) => {
    try {
      const shipment = shipments.find(s => s.id === shipmentId);
      if (!shipment) {
        showToast('Shipment not found', 'error');
        return;
      }

      // Check if FDC shipment
      if (shipment.warehouse_id === 9 && shipment.shipment_meta_1 && !force) {
        try {
          const fdcResponse = await api.post('/fdc/delete-fdc-order', { id: shipment.shipment_meta_1 });
          if (fdcResponse.data.success) {
            showToast(fdcResponse.data.message, 'success');
          } else {
            showToast(fdcResponse.data.message, 'warning');
            setDeleteConfirmMessage(`Could not delete from FDC: ${fdcResponse.data.message}. Do you want to delete the ERP shipment entry anyways?`);
            setPendingDelete({ order_id: order.id, shipment_id: shipmentId });
            setShowDeleteConfirm(true);
            return;
          }
        } catch (fdcError: any) {
          showToast('Failed to connect to FDC', 'error');
          console.error('FDC delete error:', fdcError);
          return;
        }
      }

      showToast('Syncing with WooCommerce...', 'syncing');
      console.log(`Deleting shipment ${shipmentId} for order ${order.id}`);
      const response = await api.post('/shipments', {
        action: 'delete',
        order_id: order.id,
        shipment_id: shipmentId,
        force,
      });
      setShipments(shipments.filter(s => s.id !== shipmentId));
      await syncWithWooCommerce({
        tracking_numbers: [{
          tracking_number: response.data.deletedShipment.tracking_number,
          carrier: response.data.deletedShipment.carrier,
          date_shipped: response.data.deletedShipment.date_shipped,
          delete: true,
        }],
      });
      setAlreadyShippedWarning(null);
      await refreshOrders();
      await fetchData();
      showToast('Shipment deleted successfully', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to delete shipment';
      setError(errorMessage);
      console.error('Delete shipment error:', err);
      showToast(errorMessage, 'error');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleConfirmDelete = async () => {
    if (pendingDelete) {
      await handleDeleteShipment(pendingDelete.shipment_id, true);
      setShowDeleteConfirm(false);
      setPendingDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setPendingDelete(null);
  };

  const handleItemSelection = (instanceKey: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(instanceKey)) {
        newSet.delete(instanceKey);
      } else {
        newSet.add(instanceKey);
      }
      return newSet;
    });
  };

  const handleNonItemShipmentSelection = (shipmentId: number) => {
    setSelectedNonItemShipments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(shipmentId)) {
        newSet.delete(shipmentId);
      } else {
        newSet.add(shipmentId);
      }
      return newSet;
    });
  };

  const handleAssignItems = async () => {
    if (!assigningShipmentId || assignmentItems.length === 0) {
      setError('Please select items to assign');
      showToast('Please select items to assign', 'error');
      setTimeout(() => setError(null), 5000);
      return;
    }
    try {
      const response = await api.post('/shipments', {
        action: 'assign_items',
        order_id: order.id,
        shipment_id: assigningShipmentId,
        data: { items: assignmentItems },
      });
      setShipments(shipments.map(s => s.id === assigningShipmentId ? { ...s, items: [...s.items, ...response.data] } : s));
      await syncWithWooCommerce({
        tracking_numbers: [{
          tracking_number: shipments.find(s => s.id === assigningShipmentId)?.tracking_number || '',
          carrier: shipments.find(s => s.id === assigningShipmentId)?.carrier || '',
          date_shipped: shipments.find(s => s.id === assigningShipmentId)?.date_shipped || new Date().toISOString(),
        }],
      });
      setAssignmentItems([]);
      setAssigningShipmentId(null);
      setAlreadyShippedWarning(null);
      await refreshOrders();
      await fetchData();
      showToast('Items assigned successfully', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to assign items';
      setError(errorMessage);
      console.error('Assign items error:', err);
      showToast(errorMessage, 'error');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleAddAssignmentItem = async (item: OrderItem, quantity: number) => {
    setAssignmentItems((prev) => [
      ...prev,
      {
        id: Date.now(),
        shipment_id: assigningShipmentId!,
        order_item_id: item.id,
        quantity: 1,
        product_id: item.product_id,
        product_name: item.product_name,
        sku: item.sku || null,
        gtin: item.gtin || null,
        status: 'Pending',
        scan_timestamp: null,
        scanned_by: null,
      },
    ]);
    const instanceKey = `${item.id}-0`;
  };

  const handleOpenShipNerdDialog = () => {
    if (selectedItems.size === 0) {
      setError('Please select at least one item');
      showToast('Please select at least one item', 'error');
      setTimeout(() => setError(null), 5000);
      return;
    }
    const itemsWithShipment = Array.from(selectedItems).filter(instanceKey => {
      const [itemId] = instanceKey.split('-').map(Number);
      return shipments.some(s => s.items.some(si => si.order_item_id === itemId));
    });
    if (itemsWithShipment.length > 0) {
      setShowReshipPrompt(true);
    } else {
      setShowShippingDialog(true);
    }
  };

  const handleReshipConfirm = () => {
    setShowReshipPrompt(false);
    setShowShippingDialog(true);
  };

  const syncWithWooCommerce = async (updates: {
    status?: string;
    notes?: { content: string; added_by: string; isCustomerNote?: boolean }[];
    tracking_numbers?: { tracking_number: string; carrier: string; date_shipped: string; delete?: boolean }[];
  }) => {
    try {
      console.log(`Initiating WooCommerce sync for order ${order.id} with updates:`, updates);
      const response = await api.post('/sync-woocommerce-order', {
        order_id: order.id,
        updates,
      });
      console.log(`WooCommerce sync response for order ${order.id}:`, response.data);
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to sync with WooCommerce';
      setError(errorMessage);
      console.error('Sync WooCommerce error:', err);
      throw err;
    }
  };

  const handleSave = async () => {
    try {
      console.log(`Saving changes for order ${order.id}`);
      if (orderStatus !== order.status) {
        showToast('Updating order status...', 'syncing');
        await api.post('/update-order-status', { order_id: order.id, status: orderStatus });
        try {
          await syncWithWooCommerce({ status: orderStatus });
          showToast('Order status updated successfully', 'success');
        } catch (err: any) {
          const errorMessage = err.response?.data?.error || 'Failed to sync with WooCommerce store';
          setSyncError({ updates: { status: orderStatus }, message: errorMessage });
          showToast(errorMessage, 'error');
        }
      }
      await refreshOrders();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to save changes';
      setError(errorMessage);
      console.error('Save changes error:', err);
      showToast(errorMessage, 'error');
      setTimeout(() => setError(null), 5000);
    }
  };

  const getShipmentForItem = (itemId: number, instanceKey: string) => {
    const [_, qtyIndex] = instanceKey.split('-').map(Number);
    const relevantShipments = Array.isArray(shipments) ? shipments.filter(s =>
      Array.isArray(s.items) && s.items.some(si => si.order_item_id === itemId)
    ) : [];
    if (relevantShipments.length === 0) return null;

    let instanceCount = 0;
    for (const shipment of relevantShipments) {
      let siIndex = 0;
      for (const si of shipment.items) {
        if (si.order_item_id === itemId) {
          instanceCount += si.quantity;
          if (qtyIndex < instanceCount) {
            // Found the matching shipment item
            const trackingNumbers = shipment.tracking_number ? shipment.tracking_number.split(',').map(s => s.trim()) : [];
            if (trackingNumbers.length === 0) return null;

            const totalItems = shipment.items.reduce((sum, s) => sum + s.quantity, 0);
            const itemIndex = shipment.items.slice(0, siIndex).reduce((sum, s) => sum + s.quantity, 0) + qtyIndex;

            let chipsToShow: string[];
            if (trackingNumbers.length > totalItems) {
              // More tracking numbers than items: each item shows all tracking numbers
              chipsToShow = trackingNumbers;
            } else {
              // Equal or fewer: distribute tracking numbers
              chipsToShow = [trackingNumbers[itemIndex % trackingNumbers.length]];
            }

            return chipsToShow.map(trackingNumber => (
              <div key={`${shipment.id}-${trackingNumber}`}>
                <a
                  href={getTrackingLink(trackingNumber, shipment.carrier)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`carrier-chip carrier-chip-${normalizeCarrier(shipment.carrier).toLowerCase()}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {trackingNumber}
                </a>
              </div>
            ));
          }
        }
        siIndex++;
      }
    }
    return null;
  };

  const getShipmentWarehouseForItem = (itemId: number, instanceKey: string) => {
    const [_, qtyIndex] = instanceKey.split('-').map(Number);
    const relevantShipments = Array.isArray(shipments) ? shipments.filter(s =>
      Array.isArray(s.items) && s.items.some(si => si.order_item_id === itemId)
    ) : [];
    if (relevantShipments.length === 0) return 'N/A';

    let instanceCount = 0;
    for (const shipment of relevantShipments) {
      for (const si of shipment.items) {
        if (si.order_item_id === itemId) {
          instanceCount += si.quantity;
          if (qtyIndex < instanceCount) {
            const warehouse = warehouses.find(w => w.id === shipment.warehouse_id);
            return warehouse ? warehouse.name : 'Archived Warehouse';
          }
        }
      }
    }
    return 'N/A';
  };

  const getShipmentReferenceForItem = (itemId: number, instanceKey: string) => {
    const [_, qtyIndex] = instanceKey.split('-').map(Number);
    const relevantShipments = Array.isArray(shipments) ? shipments.filter(s =>
      Array.isArray(s.items) && s.items.some(si => si.order_item_id === itemId)
    ) : [];
    if (relevantShipments.length === 0) return '';

    let instanceCount = 0;
    for (const shipment of relevantShipments) {
      for (const si of shipment.items) {
        if (si.order_item_id === itemId) {
          instanceCount += si.quantity;
          if (qtyIndex < instanceCount) {
            return shipment.shipment_reference || '';
          }
        }
      }
    }
    return '';
  };



  const hasShipmentForItem = (itemId: number) => {
    return Array.isArray(shipments) && shipments.some(s => Array.isArray(s.items) && s.items.some(si => si.order_item_id === itemId));
  };

  const getStockDisplay = (itemId: number) => {
    const stockItem = itemStocks.get(itemId);
    if (!stockItem || !stockItem.warehouse_stocks) return {};
    const displayWarehouses = warehouses.filter(w => w.barcode_shipping || w['3PL_shipping']).map(w => w.name);
    const stockMap: { [key: string]: number } = {};
    Object.entries(stockItem.warehouse_stocks)
      .filter(([warehouseName]) => displayWarehouses.includes(warehouseName))
      .forEach(([warehouseName, quantity]) => {
        stockMap[warehouseName] = quantity;
      });
    return stockMap;
  };

  const hasSufficientStockForWarehouse = (warehouseId: number) => {
    if (selectedItems.size === 0) return true;

    const warehouse = warehouses.find(w => w.id === warehouseId);
    if (!warehouse) return false;

    for (const itemKey of Array.from(selectedItems)) {
      const [itemId] = itemKey.split('-').map(Number);
      const stockItem = itemStocks.get(itemId);
      if (!stockItem || !stockItem.warehouse_stocks) return false;

      const warehouseStock = stockItem.warehouse_stocks[warehouse.name] || 0;
      if (warehouseStock <= 0) return false;
    }
    return true;
  };

  const nonItemShipments = Array.isArray(shipments) ? shipments.filter(s => Array.isArray(s.items) && s.items.length === 0) : [];

  const customerSubmittedNotes = orderNotes.filter(note => note.added_by === 'customer');
  const otherNotes = orderNotes.filter(note => note.added_by !== 'customer' && (!hideAdminNotes || note.added_by !== 'WooCommerce'));

  return (
    <div className="order-dialog-overlay">
      <div className="order-dialog" ref={dialogRef} style={showShippingDialog ? { overflowY: 'hidden' } : undefined}>
        <button className="close-button" onClick={onClose}>✕</button>
        <AddTaskButton
          className="tk-add-task-dialog"
          link={{ link_type: 'order', link_ref: String(order.id), link_label: `#${order.id} ${order.customer_name || ''}`.trim() }}
        />
        <div className="order-dialog-content">
          <div className="order-details-section address-section">
            <h2 className="order-title">Order #{order.id}</h2>
            <h2 className="customer-name">{order.customer_name}</h2>
            <h4>Billing Address</h4>
            <p>{order.customer_name}</p>
            <p>{order.customer_address}</p>
            {order.customer_address_2 && <p>{order.customer_address_2}</p>}
            <p>
              {order.customer_city}
              {order.customer_city && order.customer_state ? ', ' : ''}
              {order.customer_state} {order.customer_country}
            </p>
            <p>{order.customer_zipcode}</p>
            <h4>Shipping Address</h4>
            <div className={addressConflict ? 'address-mismatch' : ''}>
            {order.shipping_company && <p>{order.shipping_company}</p>}
            <p>{order.customer_name}</p>
            <p>{order.shipping_address}</p>
            {order.shipping_address_2 && <p>{order.shipping_address_2}</p>}
            <p>
              {order.shipping_city}
              {order.shipping_city && order.shipping_state ? ', ' : ''}
              {order.shipping_state} {order.shipping_country}

            </p>
            <p>{order.shipping_zipcode}</p>
            {order.customer_phone && <p>Phone: {order.customer_phone}</p>}
            {order.customer_email && <p>Email: {order.customer_email}</p>}
            </div>
            {addressConflict && (
              <div className="address-confirm-block">
                <button
                  className="action-button"
                  onClick={openAddressConfirm}
                  disabled={!order.customer_email}
                  title={order.customer_email ? '' : 'No customer email on this order'}
                >
                  Request address confirmation
                </button>
                {lastAddressConfirmation && (
                  <p className="address-confirm-sent">
                    Sent {new Date(lastAddressConfirmation.date_added).toLocaleString()}
                  </p>
                )}
              </div>
            )}
            {order.customer_provided_note && (
              <>
                <h4>Customer Provided Note</h4>
                <p>{order.customer_provided_note}</p>
              </>
            )}
          </div>
          <div className="order-details-section actions-section">
            <h4>Order Status</h4>
            <select
              value={orderStatus}
              onChange={(e) => setOrderStatus(e.target.value)}
              className="status-select"
            >
              <option value="completed">Completed</option>
              <option value="processing">Processing</option>
              <option value="partial-shipped">Partial-Shipped</option>
              <option value="on-hold">On-Hold</option>
              <option value="refunded">Refunded</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="add-note-container">
              <h4>Add Note</h4>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Enter order note..."
                className="note-input"
              />
              <select
                value={noteType}
                onChange={(e) => setNoteType(e.target.value as 'Customer Note' | 'Private Note')}
                className="status-select"
              >
                <option value="Private Note">Private Note</option>
                <option value="Customer Note">Customer Note</option>
              </select>
              <button className="action-button" onClick={handleAddNote} disabled={!newNote.trim()}>
                Add Note
              </button>
            </div>
            {showTrackingForm && (
              <div className="tracking-form">
                <h4>Add Shipment</h4>
                <div className="tracking-inputs">
                  <input
                    type="text"
                    value={newTrackingNumber}
                    onChange={(e) => setNewTrackingNumber(e.target.value)}
                    placeholder="Tracking Number"
                    className="tracking-input"
                  />
                  <select
                    value={newCarrier}
                    onChange={(e) => setNewCarrier(e.target.value)}
                    className="carrier-select"
                  >
                    {carriers.map((carrier) => (
                      <option key={carrier.name} value={carrier.name}>
                        {carrier.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="tracking-inputs">
                  <input
                    type="text"
                    value={newShipmentReference}
                    onChange={(e) => setNewShipmentReference(e.target.value)}
                    placeholder="Shipment Reference"
                    className="tracking-input"
                  />
                  <select
                    value={selectedWarehouse ?? ''}
                    onChange={(e) => setSelectedWarehouse(Number(e.target.value) || null)}
                    className="warehouse-select"
                  >
                    <option value="">Select Warehouse</option>
                    {warehouses.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="tracking-buttons">
                  <button
                    className="action-button"
                    onClick={() => handleAddTracking(false)}
                    disabled={!newTrackingNumber.trim() || !newCarrier || selectedWarehouse === null}
                  >
                    Save
                  </button>
                  <button
                    className="action-button"
                    onClick={() => setShowCustomerNoteDialog(true)}
                    disabled={!newTrackingNumber.trim() || !newCarrier || selectedWarehouse === null}
                  >
                    Save With Note
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="order-details-section actions-notes-section">
            <div className="notes-container">
              {customerSubmittedNotes.length > 0 && (
                <>
                  <h4>Customer Note</h4>
                  <ul className="notes-list">
                    {customerSubmittedNotes.map((note) => (
                      <li key={note.id} className="note-item">
                        <span className="note-tag customer-submitted-note" style={{ backgroundColor: userColors.get(note.added_by) || '#f8f9fa' }}>
                          {note.note}
                          <div className="note-meta">
                            {note.added_by}, {new Date(note.date_added).toLocaleString()}
                          </div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <h4>Order Notes</h4>
              <div className="checkbox-container">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={hideAdminNotes}
                    onChange={(e) => setHideAdminNotes(e.target.checked)}
                  />
                  Hide Admin Notes
                </label>
              </div>
              {otherNotes.length > 0 ? (
                <ul className="notes-list">
                  {otherNotes.map((note) => (
                    <li key={note.id} className="note-item">
                      <span className={`note-tag ${note.is_customer_note ? 'customer-note' : 'admin-note'}`} style={{ backgroundColor: userColors.get(note.added_by) || '#f8f9fa' }}>
                        {note.note}
                        <div className="note-meta">
                          {note.added_by}, {new Date(note.date_added).toLocaleString()}
                        </div>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No order notes</p>
              )}
            </div>
          </div>
        </div>
        <div className="order-items-section">
          <h4>Order Items</h4>
          {alreadyShippedWarning && (
            <div className="error-message bg-yellow-100 text-yellow-800">{alreadyShippedWarning}</div>
          )}
          <div className="order-items-table-wrapper">
          <table className="order-items-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Product Name</th>
                <th>SKU</th>
                <th>Warehouse</th>
                {warehouses.filter(w => w.barcode_shipping || w['3PL_shipping']).map(warehouse => (
                  <th key={warehouse.id}>{warehouse.name} Stock</th>
                ))}
                <th>Tracking</th>
                <th>Shipment Reference</th>
              </tr>
            </thead>
            <tbody>
              {order.items.flatMap((item: OrderItem, itemIndex: number) =>
                Array.from({ length: item.quantity }, (_, qtyIndex) => {
                  const instanceKey = `${item.id}-${qtyIndex}`;
                  const stockMap = getStockDisplay(item.id);
                  return (
                    <tr key={instanceKey}>
                      <td className="center">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(instanceKey)}
                          onChange={() => handleItemSelection(instanceKey)}
                        />
                      </td>
                      <td>{item.product_name}</td>
                      <td>{item.sku || 'N/A'}</td>
                      <td>{getShipmentWarehouseForItem(item.id, instanceKey)}</td>
                      {warehouses.filter(w => w.barcode_shipping || w['3PL_shipping']).map(warehouse => (
                        <td key={warehouse.id} className="stock-cell">
                          {stockMap[warehouse.name] ?? 'N/A'}
                        </td>
                      ))}
                      <td>{getShipmentForItem(item.id, instanceKey)}</td>
                      <td>{getShipmentReferenceForItem(item.id, instanceKey)}</td>
                    </tr>
                  );
                })
              )}
              {nonItemShipments.map((shipment) => (
                <tr key={`non-item-${shipment.id}`}>
                  <td className="center">
                    <input
                      type="checkbox"
                      checked={selectedNonItemShipments.has(shipment.id)}
                      onChange={() => handleNonItemShipmentSelection(shipment.id)}
                    />
                  </td>
                  <td></td>
                  <td></td>
                  <td>{warehouses.find(w => w.id === shipment.warehouse_id)?.name || 'N/A'}</td>
                  {warehouses.filter(w => w.barcode_shipping || w['3PL_shipping']).map(warehouse => (
                    <td key={warehouse.id} className="stock-cell"></td>
                  ))}
                  <td>
                    <a
                      href={getTrackingLink(shipment.tracking_number, shipment.carrier)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`carrier-chip carrier-chip-${normalizeCarrier(shipment.carrier).toLowerCase()}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {shipment.tracking_number}
                    </a>
                  </td>
                  <td>{shipment.shipment_reference || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {assigningShipmentId && (
            <div className="assignment-section">
              <h4>Assign Items to Shipment #{shipments.find(s => s.id === assigningShipmentId)?.tracking_number}</h4>
              <table className="assignment-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.product_name}</td>
                      <td>
                        <button
                          className="action-button"
                          onClick={() => handleAddAssignmentItem(item, 1)}
                        >
                          Add
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {assignmentItems.length > 0 && (
                <button
                  className="action-button"
                  onClick={handleAssignItems}
                >
                  Confirm Assignment
                </button>
              )}
              <button
                className="action-button cancel-button"
                onClick={() => {
                  setAssigningShipmentId(null);
                  setAssignmentItems([]);
                  setAlreadyShippedWarning(null);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        {error && <div className="error-message">{error}</div>}
        {!showShippingDialog && <div className="order-dialog-actions">
          {(selectedItems.size > 0 || selectedNonItemShipments.size > 0) && (
            <div className="dynamic-actions">
              <button
                className="action-button dynamic-button"
                onClick={() => setShowTrackingForm(true)}
              >
                Manually Add Shipment
              </button>
              <button
                className="action-button dynamic-button"
                onClick={() => {
                  setSelectedWarehouse(1);
                  handleOpenShipNerdDialog();
                }}
              >
                Send to Gelinas
              </button>
              <button
                className="action-button dynamic-button"
                onClick={() => {
                  setSelectedWarehouse(2);
                  handleOpenShipNerdDialog();
                }}
              >
                Send to MPP
              </button>
              {warehouses.filter(w => w['3PL_shipping']).map(warehouse => (
                <button
                  key={`send-to-${warehouse.id}`}
                  className={`action-button dynamic-button ${!hasSufficientStockForWarehouse(warehouse.id) ? 'disabled' : ''}`}
                  disabled={!hasSufficientStockForWarehouse(warehouse.id)}
                  onClick={() => {
                    setSelectedWarehouse(warehouse.id);
                    setShowShippingDialog(true);
                  }}
                >
                  Send To {warehouse.name}
                </button>
              ))}
              {Array.from(selectedItems).map(instanceKey => {
                const [itemId] = instanceKey.split('-').map(Number);
                return (
                  hasShipmentForItem(itemId) && (
                    <button
                      key={`delete-${instanceKey}`}
                      className="action-button dynamic-button delete-button"
                      onClick={() => {
                        const shipmentId = shipments.find(s => Array.isArray(s.items) && s.items.some(si => si.order_item_id === itemId))?.id;
                        if (shipmentId) handleDeleteShipment(shipmentId);
                      }}
                    >
                      Delete Shipment
                    </button>
                  )
                );
              })}
              {Array.from(selectedNonItemShipments).map(shipmentId => (
                <button
                  key={`delete-non-item-${shipmentId}`}
                  className="action-button dynamic-button delete-button"
                  onClick={() => handleDeleteShipment(shipmentId)}
                >
                  Delete Shipment
                </button>
              ))}
              {Array.from(selectedNonItemShipments).map(shipmentId => (
                <button
                  key={`assign-${shipmentId}`}
                  className="action-button dynamic-button"
                  onClick={() => setAssigningShipmentId(shipmentId)}
                >
                  Assign Items
                </button>
              ))}
            </div>
          )}
          <button className="action-button save-button" onClick={handleSave}>
            Save
          </button>
        </div>}
        {showCustomerNoteDialog && (
          <div className="customer-note-dialog-overlay">
            <div className="customer-note-dialog">
              <h4>Add Customer Note</h4>
              <textarea
                value={customerNote}
                onChange={(e) => setCustomerNote(e.target.value)}
                className="customer-note-input"
              />
              <div className="customer-note-buttons">
                <button
                  className="action-button cancel-button"
                  onClick={() => {
                    setShowCustomerNoteDialog(false);
                    setCustomerNote('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="action-button"
                  onClick={() => handleAddTracking(true)}
                >
                  Save Note
                </button>
              </div>
            </div>
          </div>
        )}
        {showAddressConfirm && (
          <div className="customer-note-dialog-overlay">
            <div className="customer-note-dialog">
              <h4>Request Address Confirmation</h4>
              <p>
                WooCommerce will email this to {order.customer_email} as a customer note. You can edit it first.
              </p>
              {lastAddressConfirmation && (
                <p className="address-confirm-sent">
                  Already sent {new Date(lastAddressConfirmation.date_added).toLocaleString()}.
                </p>
              )}
              <textarea
                value={addressConfirmText}
                onChange={(e) => setAddressConfirmText(e.target.value)}
                className="customer-note-input address-confirm-input"
              />
              <div className="customer-note-buttons">
                <button
                  className="action-button cancel-button"
                  onClick={() => setShowAddressConfirm(false)}
                  disabled={sendingAddressConfirm}
                >
                  Cancel
                </button>
                <button
                  className="action-button"
                  onClick={handleSendAddressConfirm}
                  disabled={sendingAddressConfirm || !addressConfirmText.trim()}
                >
                  {sendingAddressConfirm ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showReshipPrompt && (
          <div className="customer-note-dialog-overlay">
            <div className="customer-note-dialog">
              <h4>Confirm Reshipment</h4>
              <p>Some selected items have already been shipped. Are you sure you want to continue?</p>
              <div className="customer-note-buttons">
                <button
                  className="action-button cancel-button"
                  onClick={() => setShowReshipPrompt(false)}
                >
                  Cancel
                </button>
                <button
                  className="action-button"
                  onClick={handleReshipConfirm}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
        {showShippingDialog && selectedWarehouse && (
          <ShippingDialog
            order={order}
            selectedItems={selectedItemsArray}
            onClose={() => setShowShippingDialog(false)}
            refreshOrders={refreshOrders}
            warehouseId={selectedWarehouse}
            warehouses={warehouses}
          />
        )}
        {showDeleteConfirm && (
          <div className="confirm-dialog-overlay">
            <div className="confirm-dialog">
              <h3>Confirm Deletion</h3>
              <p>{deleteConfirmMessage}</p>
              <div className="confirm-dialog-actions">
                <button
                  className="action-button cancel-button"
                  onClick={handleCancelDelete}
                >
                  No, Keep Shipment
                </button>
                <button
                  className="action-button"
                  onClick={handleConfirmDelete}
                >
                  Yes, Delete Anyways
                </button>
              </div>
            </div>
          </div>
        )}
        {syncError && (
          <div className="confirm-dialog-overlay">
            <div className="confirm-dialog">
              <h3>Sync Failed</h3>
              <p>{syncError.message}</p>
              <div className="confirm-dialog-actions">
                <button
                  className="action-button cancel-button"
                  onClick={() => setSyncError(null)}
                >
                  Cancel
                </button>
                <button
                  className="action-button"
                  onClick={async () => {
                    try {
                      await syncWithWooCommerce(syncError.updates);
                      setSyncError(null);
                      showToast('Synced with WooCommerce successfully', 'success');
                    } catch (err: any) {
                      showToast('Retry failed', 'error');
                    }
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailsDialog;